import { Link, Navigate, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useMemo, useState } from 'react'
import type { CaseCreatedResponse } from '@tex/finance-shared'
import { BuyerFields, DeliveryFields, EquipmentEditor, PricingEditor, PricingSummary } from '../components/case-form'
import { IssuedLink } from '../components/participants-card'
import { Alert, Button, Card, Field, Textarea } from '../components/ui'
import { ApiError, errorMessage } from '../lib/api'
import {
  type BuyerDraft,
  type DeliveryDraft,
  emptyBuyer,
  emptyDelivery,
  emptyEquipment,
  emptyPricing,
  type EquipmentDraft,
  FormError,
  type PricingDraft,
  pricingReasonMessage,
  previewPricing,
  toBuyerInput,
  toDeliveryInput,
  toEquipmentInput,
  toPricingInput,
} from '../lib/case-form'
import { useCreateCase } from '../lib/cases'
import { isTexStaff, useMe } from '../lib/session'

export function CaseNewPage({ search }: { search: { dealer?: string } }) {
  const me = useMe()
  const memberships = me.data?.dealers ?? []
  const dealerId = search.dealer ?? (memberships.length === 1 ? memberships[0]?.dealer_id : undefined)
  const membership = memberships.find((m) => m.dealer_id === dealerId)

  if (me.data === null) return <Navigate to="/sign-in" />
  if (!me.data) return <p className="text-sm text-neutral-500">Loading…</p>
  if (isTexStaff(me.data)) return <Alert tone="error">Requests are opened by dealership team members, not Tex staff.</Alert>
  if (!dealerId || !membership) return <Navigate to="/cases" search={{}} />
  if (membership.status !== 'active') {
    return <Alert tone="error">Your dealership must be active before it can open financing requests.</Alert>
  }
  return <NewRequestForm dealerId={dealerId} />
}

function NewRequestForm({ dealerId }: { dealerId: string }) {
  const navigate = useNavigate()
  const [idempotencyKey] = useState(() => crypto.randomUUID())
  const create = useCreateCase(dealerId, idempotencyKey)

  const [buyer, setBuyer] = useState<BuyerDraft>(emptyBuyer)
  const [equipment, setEquipment] = useState<EquipmentDraft[]>(() => [emptyEquipment()])
  const [pricing, setPricing] = useState<PricingDraft>(emptyPricing)
  const [delivery, setDelivery] = useState<DeliveryDraft>(emptyDelivery)
  const [note, setNote] = useState('')
  const [inviteNow, setInviteNow] = useState(true)
  const [created, setCreated] = useState<CaseCreatedResponse | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const preview = useMemo(() => previewPricing(equipment, pricing), [equipment, pricing])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setFormError(null)
    try {
      const input = {
        buyer: toBuyerInput(buyer),
        equipment: toEquipmentInput(equipment),
        pricing: toPricingInput(pricing),
        delivery: toDeliveryInput(delivery),
        dealer_note: note.trim() || null,
        ...(inviteNow ? { invite: { send: true as const } } : {}),
      }
      if (!preview.ok) {
        setFormError(preview.message)
        return
      }
      create.mutate(input, {
        onSuccess: (result) => {
          if (result.invitation) setCreated(result)
          else void navigate({ to: '/cases/$caseId', params: { caseId: result.case.id } })
        },
      })
    } catch (err) {
      if (err instanceof FormError) setFormError(err.message)
      else throw err
    }
  }

  const apiMessage = create.error
    ? create.error instanceof ApiError && create.error.code === 'pricing_invalid'
      ? (pricingReasonMessage(create.error.reason) ?? errorMessage(create.error))
      : errorMessage(create.error)
    : null

  if (created?.invitation) {
    return (
      <div className="space-y-6">
        <div>
          <Link to="/cases" search={{}} className="text-sm text-neutral-500 hover:text-neutral-800">
            ← Requests
          </Link>
          <h1 className="mt-1 font-display text-3xl font-bold text-neutral-900">Request {created.case.reference} opened</h1>
        </div>
        <IssuedLink issued={created.invitation} />
        <div className="flex justify-end">
          <Button type="button" onClick={() => void navigate({ to: '/cases/$caseId', params: { caseId: created.case.id } })}>
            Open request
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={submit} className="space-y-6">
      <div>
        <Link to="/cases" search={{}} className="text-sm text-neutral-500 hover:text-neutral-800">
          ← Requests
        </Link>
        <h1 className="mt-1 font-display text-3xl font-bold text-neutral-900">New financing request</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Enter what you know about the buyer and the equipment. The buyer completes their own application privately; you will not see their financials.
        </p>
      </div>

      <Card title="Buyer">
        <BuyerFields draft={buyer} set={setBuyer} disabled={create.isPending} />
      </Card>

      <Card title="Equipment">
        <EquipmentEditor lines={equipment} set={setEquipment} disabled={create.isPending} />
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
        <Card title="Pricing">
          <PricingEditor draft={pricing} set={setPricing} disabled={create.isPending} />
        </Card>
        <Card title="Totals">
          <PricingSummary preview={preview} />
        </Card>
      </div>

      <Card title="Delivery and notes">
        <div className="space-y-3">
          <DeliveryFields draft={delivery} set={setDelivery} disabled={create.isPending} />
          <Field label="Note for Tex" hint="Anything the reviewer should know. Not shown to the buyer.">
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} disabled={create.isPending} />
          </Field>
        </div>
      </Card>

      <Card title="Borrower access">
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={inviteNow} onChange={(e) => setInviteNow(e.target.checked)} disabled={create.isPending} />
          <span>
            Send the buyer their private application link now
            {buyer.contact_email.trim() && (
              <>
                {' '}
                (to <span className="font-mono">{buyer.contact_email.trim().toLowerCase()}</span>)
              </>
            )}
            . You can also do this later from the request.
          </span>
        </label>
      </Card>

      {formError && <Alert tone="error">{formError}</Alert>}
      {apiMessage && <Alert tone="error">{apiMessage}</Alert>}

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="secondary" onClick={() => void navigate({ to: '/cases', search: {} })} disabled={create.isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Opening…' : 'Open request'}
        </Button>
      </div>
    </form>
  )
}
